import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import {
  money,
  ratePerMinute,
  summarizeProject,
  units as makeUnits,
  type Resource,
  type ResourceId,
  type ResourceRateTables,
} from '@bigint-project/core';

import { ResourceSheet } from '../components/resource';
import { ResourceRateTablesDialog } from '../components/resource-rate-tables';
import { ProjectBudgetDialog } from '../components/project-budget';
import type { ResourceSheetField } from '../components/resource/types';
import { buildResourceSheetRows } from '../integration/adapters';
import { useProjectStore } from '../store/project-store';
import { EmptyState } from './EmptyState';
import { selectCurrentCostResult } from '../integration/derivations';

/** 解析數字輸入；空白或非數字一律視為「不變更」。 */
function parseNumber(raw: string): number | null {
  const value = Number(raw.replace(/[,\s]/g, ''));
  return Number.isFinite(value) ? value : null;
}

/**
 * 資源工作表視圖。
 *
 * `ResourceSheet` 的 `onFieldChange` 一律送字串（展示層不猜型別），因此欄位
 * 對應到領域模型的轉換全在這裡：費率是「每小時」顯示、「每分鐘」儲存。
 */
export function ResourceSheetView({ readOnly = false }: { readonly readOnly?: boolean }): ReactElement {
  const project = useProjectStore((state) => state.project);
  const selectedResourceIds = useProjectStore((state) => state.selectedResourceIds);
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [rateTablesResourceId, setRateTablesResourceId] = useState<ResourceId | null>(null);
  const [isProjectBudgetOpen, setProjectBudgetOpen] = useState(false);
  const addResourceButtonRef = useRef<HTMLButtonElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const cancelDeleteButtonRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreDeleteButtonFocus = useRef(false);
  const rows = useMemo(() => buildResourceSheetRows(project), [project]);
  const selectedIds = useMemo(
    () => selectedResourceIds.filter((id) => project.resources.some((resource) => resource.id === id)),
    [project.resources, selectedResourceIds],
  );
  const rateTablesResource = rateTablesResourceId === null
    ? null
    : project.resources.find((resource) => resource.id === rateTablesResourceId) ?? null;
  const selectedResource = selectedIds.length === 1
    ? project.resources.find((resource) => resource.id === selectedIds[0]) ?? null
    : null;
  const currentCost = useMemo(() => selectCurrentCostResult(project), [project]);
  const summary = useMemo(() => summarizeProject(project), [project]);
  const baselineBudget = project.baselines.find((baseline) => baseline.number === 0)?.budgetAssignments;

  const handleFieldChange = useCallback(
    (resourceId: ResourceId, field: ResourceSheetField, raw: string): void => {
      const patch = buildResourcePatch(field, raw);
      if (patch === null) return;
      useProjectStore.getState().updateResource(resourceId, patch);
    },
    [],
  );

  const handleAddResource = useCallback((): void => {
    useProjectStore.getState().addResource();
  }, []);

  const handleAddBudgetResource = useCallback((): void => {
    useProjectStore.getState().addResource({ name: '新增預算資源', isBudget: true });
  }, []);

  const handleSelectionChange = useCallback(
    (resourceId: ResourceId, checked: boolean): void => {
      const nextIds = checked
        ? [...selectedIds, resourceId]
        : selectedIds.filter((selectedId) => selectedId !== resourceId);
      useProjectStore.getState().selectResources(nextIds);
    },
    [selectedIds],
  );

  const handleDeleteSelected = useCallback((): void => {
    if (selectedIds.length === 0) return;
    setDeleteConfirmationOpen(true);
  }, [selectedIds]);

  const closeDeleteConfirmation = useCallback((): void => {
    shouldRestoreDeleteButtonFocus.current = true;
    setDeleteConfirmationOpen(false);
  }, []);

  const confirmDeleteSelected = useCallback((): void => {
    if (selectedIds.length === 0) {
      closeDeleteConfirmation();
      return;
    }
    useProjectStore.getState().deleteResources(selectedIds as ResourceId[]);
    useProjectStore.getState().selectResources([]);
    closeDeleteConfirmation();
  }, [closeDeleteConfirmation, selectedIds]);

  useEffect(() => {
    if (isDeleteConfirmationOpen) {
      cancelDeleteButtonRef.current?.focus();
    } else if (shouldRestoreDeleteButtonFocus.current) {
      if (deleteButtonRef.current !== null && !deleteButtonRef.current.disabled) {
        deleteButtonRef.current.focus();
      } else {
        addResourceButtonRef.current?.focus();
      }
      shouldRestoreDeleteButtonFocus.current = false;
    }
  }, [isDeleteConfirmationOpen]);

  const handleConfirmationKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDeleteConfirmation();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [href]');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [closeDeleteConfirmation],
  );

  if (project.resources.length === 0) {
    return (
      <EmptyState
        title="尚無資源"
        description="建立資源後，即可在資源工作表中編輯費率、可用量與日曆。"
        action={(
          <div className="flex gap-2">
            <button type="button" ref={addResourceButtonRef} disabled={readOnly} onClick={handleAddResource} className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50">
              新增資源
            </button>
            <button type="button" disabled={readOnly} onClick={handleAddBudgetResource} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium disabled:opacity-50 dark:border-slate-600">
              新增預算資源
            </button>
          </div>
        )}
        testId="resource-sheet-empty"
      />
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden rounded border border-slate-200 dark:border-slate-700" aria-readonly={readOnly}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/40">
        <button
          type="button"
          ref={addResourceButtonRef}
          disabled={readOnly}
          onClick={handleAddResource}
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          新增資源
        </button>
        <button type="button" disabled={readOnly} onClick={handleAddBudgetResource} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium disabled:opacity-50 dark:border-slate-600">
          新增預算資源
        </button>
        <button type="button" disabled={readOnly} onClick={() => setProjectBudgetOpen(true)} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium disabled:opacity-50 dark:border-slate-600">
          專案預算{project.budgetAssignments?.length ? ` (${project.budgetAssignments.length})` : ''}
        </button>
        <button
          type="button"
          disabled={readOnly || selectedResource === null || selectedResource.isBudget === true}
          onClick={() => setRateTablesResourceId((selectedIds[0] as ResourceId | undefined) ?? null)}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
        >
          費率表 A–E
        </button>
        <button
          type="button"
          ref={deleteButtonRef}
          onClick={handleDeleteSelected}
          disabled={readOnly || selectedIds.length === 0}
          className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
        >
          刪除選取項目{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
        </button>
      </div>
      <div className="min-h-0 flex-1">
        <fieldset disabled={readOnly} className="h-full border-0 p-0">
          <ResourceSheet
          rows={rows}
          currency={project.currency}
          calendarOptions={project.calendars.map(({ id, name }) => ({ id, name }))}
          selectedResourceIds={selectedIds as ResourceId[]}
          onSelectionChange={handleSelectionChange}
          onBudgetChange={(resourceId, isBudget) => {
            useProjectStore.getState().setResourceBudgetStatus(resourceId, isBudget);
          }}
          onCalendarChange={(resourceId, calendarId) => {
            useProjectStore.getState().updateResource(resourceId, { calendarId });
          }}
          onFieldChange={handleFieldChange}
          />
        </fieldset>
      </div>
      {!readOnly && isDeleteConfirmationOpen ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="resource-delete-confirmation-title"
          aria-describedby="resource-delete-confirmation-description"
          className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/30 p-4"
          onKeyDown={handleConfirmationKeyDown}
        >
          <div className="w-full max-w-md rounded bg-white p-5 shadow-xl dark:bg-slate-800">
            <h2 id="resource-delete-confirmation-title" className="text-base font-semibold">
              刪除選取的資源？
            </h2>
            <p id="resource-delete-confirmation-description" className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              將刪除 {selectedIds.length} 個資源，以及它們相關的資源指派。此操作可透過復原功能還原。
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                ref={cancelDeleteButtonRef}
                onClick={closeDeleteConfirmation}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium dark:border-slate-600"
              >
                取消
              </button>
              <button
                type="button"
                onClick={confirmDeleteSelected}
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
              >
                刪除 {selectedIds.length} 個資源
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {readOnly || rateTablesResource === null ? null : (
        <ResourceRateTablesDialog
          resource={rateTablesResource}
          open
          onCancel={() => setRateTablesResourceId(null)}
          onSave={(rateTables: ResourceRateTables) => {
            useProjectStore.getState().updateResource(rateTablesResource.id, { rateTables });
            setRateTablesResourceId(null);
          }}
        />
      )}
      <ProjectBudgetDialog
        isOpen={!readOnly && isProjectBudgetOpen}
        resources={project.resources}
        existingBudgetAssignments={project.budgetAssignments ?? []}
        currency={project.currency}
        baselineSnapshot={baselineBudget}
        currentTotalCost={currentCost?.total.cost ?? money(0)}
        currentTotalWork={summary.totalWork}
        onCancel={() => setProjectBudgetOpen(false)}
        onSave={(assignments) => {
          useProjectStore.getState().setProjectBudgetAssignments(assignments);
          setProjectBudgetOpen(false);
        }}
      />
    </div>
  );
}

function buildResourcePatch(field: ResourceSheetField, raw: string): Partial<Resource> | null {
  switch (field) {
    case 'name':
      return raw.trim().length === 0 ? null : { name: raw.trim() };
    case 'initials':
      return { initials: raw.trim() };
    case 'group':
      return { group: raw.trim() };
    case 'type': {
      const value = raw.trim();
      return value === 'Work' || value === 'Material' || value === 'Cost' ? { type: value } : null;
    }
    case 'accrueAt': {
      const value = raw.trim();
      return value === 'Start' || value === 'End' || value === 'Prorated' ? { accrueAt: value } : null;
    }
    case 'maxUnits': {
      const value = parseNumber(raw);
      return value === null || value < 0 ? null : { maxUnits: makeUnits(value) };
    }
    case 'standardRate': {
      const value = parseNumber(raw);
      return value === null || value < 0 ? null : { standardRate: ratePerMinute(value / 60) };
    }
    case 'overtimeRate': {
      const value = parseNumber(raw);
      return value === null || value < 0 ? null : { overtimeRate: ratePerMinute(value / 60) };
    }
    case 'costPerUse': {
      const value = parseNumber(raw);
      return value === null || value < 0 ? null : { costPerUse: money(value) };
    }
    case 'calendarName':
      // 日曆是以 id 參照的實體，不能用顯示名稱回寫；改名需要專屬的日曆編輯 UI。
      return null;
    default:
      return null;
  }
}
